package com.writerslab

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/** Which category of analysis a block belongs to. Drives the colour
 *  and label of [BlockKindBadge] so a user can tell at a glance whether
 *  the numbers in a block came from formal text statistics or from an
 *  AI read-through. */
enum class BlockKind { STATIC, AI }

/**
 * Small uppercase eyebrow that tags a block as either statistics-only
 * (violet) or AI-driven (gold). Place as the first child of a card or
 * inline above the section title so the kind is obvious before reading
 * any numbers.
 */
@Composable
fun BlockKindBadge(kind: BlockKind) {
    val s = LocalStrings.current
    val text = when (kind) {
        BlockKind.STATIC -> s.blockKindStatic
        BlockKind.AI -> s.blockKindAi
    }
    val color = when (kind) {
        BlockKind.STATIC -> WLColors.Primary
        BlockKind.AI -> WLColors.Secondary
    }
    Span({
        style {
            typography(TextLevel.LabelSmall)
            fg(color)
            property("text-transform", "uppercase")
            property("letter-spacing", "0.6px")
            property("font-weight", "600")
        }
    }) { Text(text) }
}

/**
 * «?» icon button that opens a modal explaining the block it sits in.
 * Click stops propagation so it never triggers the parent block's
 * expand/collapse toggle. Place inline inside header rows; for
 * blocks without a header use [BlockHelpCorner].
 *
 * Pass a [kind] when the block is part of the analysis spectrum — the
 * modal then leads with a coloured eyebrow so a user reading the dialog
 * also sees the static-vs-AI tag they recognise from the left stripe.
 *
 * [id] is the analytics key — every call site must add an entry to
 * [AnalyticsEvents.HelpBlock] before passing it here.
 */
@Composable
fun BlockHelp(
    title: String,
    body: String,
    kind: BlockKind? = null,
    id: AnalyticsEvents.HelpBlock,
) {
    var open by remember { mutableStateOf(false) }
    val analytics = LocalAnalyticsEvents.current
    Div({
        attr("aria-label", title)
        attr("title", title)
        attr("role", "button")
        attr("tabindex", "0")
        style {
            property("width", "22px")
            property("height", "22px")
            property("border-radius", "50%")
            property("display", "inline-flex")
            property("align-items", "center")
            property("justify-content", "center")
            bg(WLColors.SurfaceContainerHigh)
            fg(WLColors.OnSurfaceVariant)
            property("font-size", "13px")
            property("font-weight", "600")
            property("line-height", "1")
            property("flex-shrink", "0")
            clickableCursor()
        }
        onClick { ev ->
            // Block headers above us are clickable to expand/collapse — stop
            // the event so a "?" click never toggles the section.
            ev.stopPropagation()
            if (!open) analytics.helpModalOpened(id)
            open = true
        }
    }) {
        Text("?")
    }
    if (open) {
        MdDialog(
            open = true,
            onClose = { open = false },
            headline = title,
            headlineTrailing = {
                Div({
                    attr("aria-label", "Close")
                    attr("role", "button")
                    attr("tabindex", "0")
                    style {
                        property("width", "32px")
                        property("height", "32px")
                        property("border-radius", "50%")
                        property("display", "inline-flex")
                        property("align-items", "center")
                        property("justify-content", "center")
                        fg(WLColors.OnSurfaceVariant)
                        property("font-size", "22px")
                        property("line-height", "1")
                        clickableCursor()
                    }
                    onClick { ev ->
                        // Don't bubble — the dialog's content can sit in
                        // an ancestor with its own click handler (e.g. a
                        // section header that toggles expand/collapse).
                        ev.stopPropagation()
                        open = false
                    }
                }) {
                    Text("\u00D7")
                }
            },
        ) {
            Div({
                style {
                    flexCol(gapPx = 12)
                    property("max-width", "560px")
                }
            }) {
                Span({
                    style {
                        typography(TextLevel.BodyMedium)
                        fg(WLColors.OnSurface)
                        property("white-space", "pre-line")
                        property("display", "block")
                    }
                }) {
                    Text(body)
                }
            }
        }
    }
}

/**
 * [BlockHelp] absolutely positioned in the top-right corner of its
 * parent. Parent must be a positioning context (`position: relative`
 * or `clickableCursor()` which sets it). Use this for cards that
 * don't have a header row to inline the icon into.
 */
@Composable
fun BlockHelpCorner(
    title: String,
    body: String,
    kind: BlockKind? = null,
    id: AnalyticsEvents.HelpBlock,
) {
    Div({
        style {
            property("position", "absolute")
            property("top", "10px")
            property("right", "10px")
            property("z-index", "1")
        }
    }) {
        BlockHelp(title, body, kind, id)
    }
}
